import React, { useState } from 'react';
import { ArrowRight, TrendingUp, Users, Award, CheckCircle, Star, Target, GraduationCap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Careers = () => {
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    referral_name: '',
    why_join: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/applications`, applicationForm);
      toast.success('Application submitted successfully! We\'ll be in touch soon.');
      setApplicationForm({
        name: '',
        email: '',
        phone: '',
        referral_name: '',
        why_join: ''
      });
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Uncapped Earnings',
      description: 'Performance-based compensation with no ceiling on your income potential.'
    },
    {
      icon: GraduationCap,
      title: 'Professional Development',
      description: 'Continuous training, certifications, and leadership development programs.'
    },
    {
      icon: Users,
      title: 'Supportive Community',
      description: 'Join a team of like-minded professionals committed to mutual success.'
    },
    {
      icon: Target,
      title: 'Flexible Schedule',
      description: 'Work-life balance with flexibility to manage your own schedule.'
    },
    {
      icon: Award,
      title: 'Recognition Programs',
      description: 'Regular recognition and rewards for top performers and achievements.'
    },
    {
      icon: Award,
      title: 'Career Advancement',
      description: 'Clear pathways for growth and leadership opportunities within the organization.'
    }
  ];

  const testimonials = [
    {
      name: 'Zeel Bardoliwala',
      role: 'Associate',
      image: 'https://customer-assets.emergentagent.com/job_wealth-builders-6/artifacts/vbg91bfi_Untitled%20design%20%282%29.jpg',
      quote: 'Joining Empower Wealth Builders transformed my career. The support, training, and growth opportunities are unmatched.'
    },
    {
      name: 'Shivam Agnihotri',
      role: 'Senior Associate',
      image: 'https://customer-assets.emergentagent.com/job_wealth-builders-6/artifacts/tlvrh08o_Untitled%20design%20%284%29.jpg',
      quote: 'I\'ve found a family here. The culture of excellence and the genuine care for team members makes this an incredible place to work.'
    },
    {
      name: 'Harsh Mehta',
      role: 'Senior Associate',
      image: 'https://customer-assets.emergentagent.com/job_wealth-builders-6/artifacts/wpfz2enb_Untitled%20design%20%283%29.jpg',
      quote: 'The leadership team invests in your success from day one. I\'ve grown more here in 2 years than in my previous 10 years combined.'
    }
  ];

  return (
    <div data-testid="careers-page" className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoYW5kc2hha2UlMjBidXNpbmVzcyUyMGRlYWx8ZW58MHx8fHwxNzcxNjUyMzA4fDA&ixlib=rb-4.1.0&q=85"
            alt="Professional Team"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#050505]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-6">Join Our Team</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-8">
              Build Your <span className="text-gold-gradient">Career</span> While Building Wealth
            </h1>
            <p className="font-body text-white/70 text-base sm:text-lg leading-relaxed">
              We're looking for ambitious individuals who want to make a difference. Join our team and be part of a movement that's transforming lives through financial education and leadership development.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-4">Why Join Us</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Benefits & <span className="text-gold-gradient">Perks</span>
            </h2>
            <p className="font-body text-white/60 max-w-2xl mx-auto">
              We invest in our team's success with comprehensive benefits and a supportive work environment.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                data-testid={`benefit-card-${index}`}
                className="group p-8 bg-white/[0.02] border border-white/5 hover:border-gold-500/30 transition-all duration-500"
              >
                <benefit.icon className="w-10 h-10 text-gold-500 mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="font-heading text-xl text-white mb-4">{benefit.title}</h3>
                <p className="font-body text-white/50 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-4">Team Stories</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              What Our <span className="text-gold-gradient">Team</span> Says
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                data-testid={`testimonial-${index}`}
                className="p-8 bg-white/[0.02] border border-white/5"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-gold-500 text-gold-500" />
                  ))}
                </div>
                <p className="font-body text-white/70 text-sm leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-heading text-white">{testimonial.name}</div>
                    <div className="font-body text-gold-500 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-4">Apply Now</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Start Your <span className="text-gold-gradient">Journey</span>
              </h2>
              <p className="font-body text-white/60 text-base sm:text-lg leading-relaxed mb-8">
                Ready to take the next step in your career? Fill out the application form and our team will get in touch with you shortly.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-gold-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-heading text-lg text-white mb-1">Quick Response</h4>
                    <p className="font-body text-white/50 text-sm">We review applications within 48 hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-gold-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-heading text-lg text-white mb-1">Overview Call</h4>
                    <p className="font-body text-white/50 text-sm">Get to know our team and culture firsthand</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-gold-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-heading text-lg text-white mb-1">Onboarding Support</h4>
                    <p className="font-body text-white/50 text-sm">Comprehensive training to set you up for success</p>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 bg-white/[0.02] border border-white/5 p-8 lg:p-10">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block font-body text-sm text-white/70 mb-2">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={applicationForm.name}
                    onChange={(e) => setApplicationForm({ ...applicationForm, name: e.target.value })}
                    data-testid="application-name-input"
                    className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50 transition-colors"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm text-white/70 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={applicationForm.email}
                    onChange={(e) => setApplicationForm({ ...applicationForm, email: e.target.value })}
                    data-testid="application-email-input"
                    className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50 transition-colors"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block font-body text-sm text-white/70 mb-2">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={applicationForm.phone}
                    onChange={(e) => setApplicationForm({ ...applicationForm, phone: e.target.value })}
                    data-testid="application-phone-input"
                    className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50 transition-colors"
                    placeholder="+1 (234) 567-890"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm text-white/70 mb-2">Referral Name (Optional)</label>
                  <input
                    type="text"
                    value={applicationForm.referral_name}
                    onChange={(e) => setApplicationForm({ ...applicationForm, referral_name: e.target.value })}
                    data-testid="application-referral-input"
                    className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50 transition-colors"
                    placeholder="Who referred you?"
                  />
                </div>
              </div>

              <div>
                <label className="block font-body text-sm text-white/70 mb-2">Why do you want to join us? *</label>
                <textarea
                  required
                  rows={4}
                  value={applicationForm.why_join}
                  onChange={(e) => setApplicationForm({ ...applicationForm, why_join: e.target.value })}
                  data-testid="application-why-input"
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50 transition-colors resize-none"
                  placeholder="Tell us about your motivation and what you can bring to the team..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="application-submit-btn"
                className="w-full bg-gold-500 text-black py-4 font-body font-semibold uppercase tracking-widest hover:bg-gold-400 transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
